import { Component, HostListener, OnInit, inject, signal, computed } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router, RouterLink } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MenuModule } from 'headlessui-angular';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { finalize } from 'rxjs';
import { IconModule } from '@crm-workspace/common-lib';
import { AuthService } from '../modules/auth/services/auth/auth.service';
import type { ChannelPartnerWorkspaceOption } from '../modules/auth/services/auth/auth.model';
import { ToastService } from '../core/services/toast/toast.service';
import { STORAGE_KEYS } from '../core/config/storage-keys.config';
import { StorageService } from '../core/services/storage/storage.service';
import { extractHttpErrorMessage } from '../core/utils/api-error-message';
import { resolveProfilePhotoUrl, resolveUserDisplayName } from '../utils/profile.util';
import { CopyableTextComponent } from '../shared/components/copyable-text/copyable-text.component';
import {
  NotificationService,
  type AppNotification,
} from '../core/services/notification/notification.service';

type NotificationFilter = 'all' | 'unread' | 'read';
type NotificationVisualType =
  | 'payment'
  | 'success'
  | 'failure'
  | 'warning'
  | 'booking'
  | 'document'
  | 'reminder'
  | 'user'
  | 'system';

interface NotificationGroup {
  label: 'Today' | 'Yesterday' | 'Earlier';
  notifications: AppNotification[];
}

@Component({
    selector: 'header',
    templateUrl: './header.html',
    styleUrl: './header.css',
    standalone: true,
    imports: [
        NgClass,
        RouterLink,
        FormsModule,
        MenuModule,
        TranslateModule,
        IconModule,
        CopyableTextComponent,
    ],
})
export class HeaderComponent implements OnInit {
    private readonly authService = inject(AuthService);
    private readonly toast = inject(ToastService);
    private readonly storageService = inject(StorageService);
    readonly notificationsService = inject(NotificationService);

    store: any;
    isScrolled = false;
    search = false;
    activeDropdown: string[] = [];

    readonly workspaces = signal<ChannelPartnerWorkspaceOption[]>([]);
    readonly defaultWorkspaceId = signal<string | null>(null);
    readonly currentWorkspaceId = signal<string | null>(null);
    readonly workspacesLoading = signal(false);
    readonly workspaceSwitching = signal(false);
    readonly workspacesError = signal('');

    readonly unreadCount = computed(() => this.notificationsService.unreadCount());
    readonly unreadNotifications = computed(() =>
      this.notificationsService.unreadNotifications(),
    );
    readonly readNotifications = computed(() =>
      this.notificationsService.readNotifications(),
    );
    readonly notificationFilter = signal<NotificationFilter>('all');
    readonly allNotifications = computed(() =>
      [...this.unreadNotifications(), ...this.readNotifications()].sort(
        (first, second) =>
          new Date(second.createdAt).getTime() -
          new Date(first.createdAt).getTime(),
      ),
    );
    readonly filteredNotifications = computed(() => {
      switch (this.notificationFilter()) {
        case 'unread':
          return this.unreadNotifications();
        case 'read':
          return this.readNotifications();
        default:
          return this.allNotifications();
      }
    });
    readonly groupedNotifications = computed(() =>
      groupNotificationsByDate(this.filteredNotifications()),
    );
    readonly hasFilteredNotifications = computed(
      () => this.filteredNotifications().length > 0,
    );
    readonly isLoadingMore = computed(() => {
      const filter = this.notificationFilter();
      if (filter === 'unread') {
        return this.notificationsService.unreadLoadingMore();
      }
      if (filter === 'read') {
        return this.notificationsService.readLoadingMore();
      }
      return (
        this.notificationsService.unreadLoadingMore() ||
        this.notificationsService.readLoadingMore()
      );
    });

    messages = [
        {
            id: 1,
            image: this.sanitizer.bypassSecurityTrustHtml(
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-success-light dark:bg-success text-success dark:text-success-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg></span>`
            ),
            title: 'Congratulations!',
            message: 'Your OS has been updated.',
            time: '1hr',
        },
        {
            id: 2,
            image: this.sanitizer.bypassSecurityTrustHtml(
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-info-light dark:bg-info text-info dark:text-info-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg></span>`
            ),
            title: 'Did you know?',
            message: 'You can switch between artboards.',
            time: '2hr',
        },
        {
            id: 3,
            image: this.sanitizer.bypassSecurityTrustHtml(
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-danger-light dark:bg-danger text-danger dark:text-danger-light"> <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></span>`
            ),
            title: 'Something went wrong!',
            message: 'Send Reposrt',
            time: '2days',
        },
        {
            id: 4,
            image: this.sanitizer.bypassSecurityTrustHtml(
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-warning-light dark:bg-warning text-warning dark:text-warning-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">    <circle cx="12" cy="12" r="10"></circle>    <line x1="12" y1="8" x2="12" y2="12"></line>    <line x1="12" y1="16" x2="12.01" y2="16"></line></svg></span>`
            ),
            title: 'Warning',
            message: 'Your password strength is low.',
            time: '5days',
        },
    ];

    constructor(
        public translate: TranslateService,
        public storeData: Store<any>,
        public router: Router,
        private readonly sanitizer: DomSanitizer,
    ) {}

    ngOnInit() {
        this.initStore();
        this.updateScrolled();
        this.syncCurrentWorkspaceId();
        if (this.showWorkspaceSwitcher) {
            this.loadWorkspaces();
        }
        this.notificationsService.setTargetPortal('CHANNEL_PARTNER');
        this.notificationsService.refreshIfStale(true);
    }

    get showWorkspaceSwitcher(): boolean {
        return this.authService.hasFullPortalAccess();
    }

    get hasMultipleWorkspaces(): boolean {
        return this.workspaces().length > 1;
    }

    initStore() {
        this.storeData
            .select((d) => d.index)
            .subscribe((d) => {
                this.store = d;
            });
    }

    @HostListener('window:scroll')
    onWindowScroll(): void {
        this.updateScrolled();
    }

    private updateScrolled(): void {
        const y = document.documentElement.scrollTop || document.body.scrollTop || 0;
        this.isScrolled = y > 8;
    }

    toggleDropdown(value: string) {
        if (this.activeDropdown.includes(value)) {
            this.activeDropdown = this.activeDropdown.filter((d) => d !== value);
        } else {
            this.activeDropdown.push(value);
        }
    }

    onWorkspaceMenuToggle(): void {
        this.toggleDropdown('workspace');
        this.syncCurrentWorkspaceId();
        if (!this.workspaces().length && !this.workspacesLoading()) {
            this.loadWorkspaces();
        }
    }

    loadWorkspaces(): void {
        if (!this.showWorkspaceSwitcher || this.workspacesLoading()) {
            return;
        }
        this.workspacesLoading.set(true);
        this.workspacesError.set('');
        this.syncCurrentWorkspaceId();

        this.authService
            .listAccessibleWorkspaces()
            .pipe(finalize(() => this.workspacesLoading.set(false)))
            .subscribe({
                next: result => {
                    this.workspaces.set(result.workspaces ?? []);
                    this.defaultWorkspaceId.set(result.defaultWorkspaceId ?? null);
                },
                error: err => {
                    this.workspaces.set([]);
                    this.workspacesError.set(
                        extractHttpErrorMessage(err, 'Could not load workspaces.'),
                    );
                },
            });
    }

    currentWorkspaceName(): string {
        const currentId = this.currentWorkspaceId();
        const match = this.workspaces().find(w => w.workspaceId === currentId);
        if (match?.workspaceName?.trim()) {
            return match.workspaceName.trim();
        }
        const user = this.authService.userDetails();
        if (typeof user?.['workspaceName'] === 'string' && user['workspaceName'].trim()) {
            return user['workspaceName'].trim();
        }
        return 'Workspace';
    }

    workspaceInitials(workspace: ChannelPartnerWorkspaceOption): string {
        const name = workspace.workspaceName?.trim() || workspace.partnerLegalName?.trim() || 'W';
        const parts = name.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    }

    isCurrentWorkspace(workspaceId: string): boolean {
        return this.currentWorkspaceId() === workspaceId;
    }

    isDefaultWorkspace(workspaceId: string): boolean {
        return this.defaultWorkspaceId() === workspaceId;
    }

    switchToWorkspace(event: Event, workspace: ChannelPartnerWorkspaceOption): void {
        event.preventDefault();
        event.stopPropagation();

        if (this.workspaceSwitching() || this.isCurrentWorkspace(workspace.workspaceId)) {
            return;
        }
        if (!this.hasMultipleWorkspaces) {
            return;
        }

        this.workspaceSwitching.set(true);
        this.authService
            .switchWorkspace(workspace.workspaceId)
            .subscribe({
                next: result => {
                    this.toast.success(`Switched to ${workspace.workspaceName}.`);
                    this.authService.redirectAfterWorkspaceSwitch(result);
                },
                error: err => {
                    this.workspaceSwitching.set(false);
                    this.toast.error(
                        extractHttpErrorMessage(err, 'Could not switch workspace.'),
                    );
                },
            });
    }

    goToMyProfile(event: Event): void {
        event.preventDefault();
        void this.router.navigateByUrl('/my-profile');
    }

    goToWorkspaceSettings(event: Event): void {
        event.preventDefault();
        void this.router.navigateByUrl('/my-profile');
    }

    onSignOut(event: Event): void {
        event.preventDefault();
        this.authService.logout();
    }

    onNotificationBellClick(): void {
        this.notificationsService.refreshIfStale();
    }

    retryNotifications(event: Event): void {
        event.stopPropagation();
        this.notificationsService.refresh();
    }

    setNotificationFilter(filter: NotificationFilter, event: Event): void {
        event.stopPropagation();
        this.notificationFilter.set(filter);
    }

    markNotificationRead(id: string, event: Event): void {
        event.stopPropagation();
        this.notificationsService.markAsRead(id);
    }

    markAllNotificationsRead(event: Event): void {
        event.stopPropagation();
        this.notificationsService.markAllAsRead();
    }

    onNotificationSelect(notification: AppNotification): void {
        if (!notification.isRead) {
            this.notificationsService.markAsRead(notification.id);
        }
        const actionUrl = notification.actionUrl?.trim();
        if (!actionUrl) {
            return;
        }
        const inAppProjectUrl = toCpInAppProjectUrl(actionUrl);
        if (inAppProjectUrl) {
            void this.router.navigateByUrl(inAppProjectUrl);
            return;
        }
        if (/^https?:\/\//i.test(actionUrl)) {
            window.open(actionUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (actionUrl.startsWith('/project/')) {
            void this.router.navigateByUrl(actionUrl);
            return;
        }
        if (actionUrl.startsWith('/')) {
            void this.router.navigateByUrl(
                remapCpNotificationActionUrl(actionUrl),
            );
        }
    }

    getNotificationType(notification: AppNotification): NotificationVisualType {
        const content =
            `${notification.title} ${notification.body} ${notification.message ?? ''}`.toLowerCase();

        if (/(payment|demand|invoice|receipt|amount|₹|rupee)/.test(content)) {
            return 'payment';
        }
        if (/(approved|success|completed|confirmed)/.test(content)) {
            return 'success';
        }
        if (/(rejected|failed|cancelled|declined|error)/.test(content)) {
            return 'failure';
        }
        if (/(warning|urgent|overdue|attention)/.test(content)) {
            return 'warning';
        }
        if (/(booking|property|unit|home)/.test(content)) {
            return 'booking';
        }
        if (/(document|file|letter|agreement)/.test(content)) {
            return 'document';
        }
        if (/(reminder|scheduled|due|clock)/.test(content)) {
            return 'reminder';
        }
        if (/(user|lead|customer|agent|partner)/.test(content)) {
            return 'user';
        }
        return 'system';
    }

    onNotificationScroll(event: Event): void {
        const service = this.notificationsService;
        if (!service.isReady() || service.loading()) {
            return;
        }

        const el = event.target as HTMLElement | null;
        if (!el) {
            return;
        }

        const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
        if (!nearBottom) {
            return;
        }

        switch (this.notificationFilter()) {
            case 'unread':
                if (service.unreadHasMore()) {
                    service.loadMoreUnread();
                }
                break;
            case 'read':
                if (service.readHasMore()) {
                    service.loadMoreRead();
                }
                break;
            default:
                if (service.unreadHasMore()) {
                    service.loadMoreUnread();
                }
                if (service.readHasMore()) {
                    service.loadMoreRead();
                }
                break;
        }
    }

    userDisplayName(): string {
        return resolveUserDisplayName(this.authService.userDetails());
    }

    userEmail(): string {
        const user = this.authService.userDetails();
        return typeof user?.['email'] === 'string' ? user['email'].trim() : '';
    }

    userPhotoUrl(): string {
        const user = this.authService.userDetails();
        return resolveProfilePhotoUrl(
            user as { profile_image?: string | null; default_avatar?: string | null } | null,
        );
    }

    private syncCurrentWorkspaceId(): void {
        const stored = this.storageService.getItem<string>(STORAGE_KEYS.WORKSPACE_ID);
        if (typeof stored === 'string' && stored.trim()) {
            this.currentWorkspaceId.set(stored.trim());
            return;
        }
        const session = this.authService.userDetails();
        this.currentWorkspaceId.set(
            typeof session?.['workspaceId'] === 'string'
                ? String(session['workspaceId'])
                : null,
        );
    }
}

/** Map persisted admin/legacy deep links onto CP portal routes. */
export function remapCpNotificationActionUrl(url: string): string {
    return url
        .replace(/^\/projects\/my-projects\//, '/projects/mine/')
        .replace(/^\/pre-sale\/leads\//, '/leads/')
        .replace(/^\/pre-sale\/opportunities/, '/opportunities');
}

/** Keep public project share links inside the CP app instead of opening Admin. */
export function toCpInAppProjectUrl(url: string): string | null {
    try {
        const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
        if (parsed.pathname.startsWith('/project/')) {
            return `${parsed.pathname}${parsed.search}`;
        }
    } catch {
        return null;
    }
    return null;
}

function groupNotificationsByDate(
  notifications: AppNotification[],
): NotificationGroup[] {
  const groups = new Map<NotificationGroup['label'], AppNotification[]>([
    ['Today', []],
    ['Yesterday', []],
    ['Earlier', []],
  ]);
  const today = startOfDay(new Date());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  for (const notification of notifications) {
    const createdAt = new Date(notification.createdAt);
    const createdDay = startOfDay(createdAt);
    let label: NotificationGroup['label'] = 'Earlier';
    if (createdDay.getTime() === today.getTime()) {
      label = 'Today';
    } else if (createdDay.getTime() === yesterday.getTime()) {
      label = 'Yesterday';
    }
    groups.get(label)?.push(notification);
  }

  return Array.from(groups.entries())
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({ label, notifications: items }));
}

function startOfDay(value: Date): Date {
  if (Number.isNaN(value.getTime())) {
    return new Date(0);
  }
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}
